import { useEffect, useState } from "react";
import type { CommentDto, HiddenDto, StampDto, TopicDto } from "@sikilesi/protocol";
import { useServices } from "../components/Services";
import { ApiError } from "../lib/api";
import { SseClient } from "../lib/sse";

const MAX_PAGES = 20;

export interface TopicThread {
  topic: TopicDto | null;
  comments: CommentDto[];
  status: "loading" | "ready" | "notfound" | "error";
  setTopic: (t: TopicDto) => void;
}

/** One topic + all its public comments, kept live by the topic's SSE room. */
export function useTopicThread(id: string): TopicThread {
  const { api, streamUrl } = useServices();
  const [topic, setTopic] = useState<TopicDto | null>(null);
  const [comments, setComments] = useState<CommentDto[]>([]);
  const [status, setStatus] = useState<TopicThread["status"]>("loading");

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    setTopic(null);
    setComments([]);

    const loadAll = async () => {
      const t = await api.topic(id);
      const all: CommentDto[] = [];
      let cursor: string | null = null;
      for (let i = 0; i < MAX_PAGES; i++) {
        const page = await api.comments(id, cursor);
        all.push(...page.items);
        cursor = page.next_cursor;
        if (!cursor) break;
      }
      return { t, all };
    };

    const refresh = () =>
      loadAll().then(
        ({ t, all }) => {
          if (cancelled) return;
          setTopic(t);
          setComments(all);
          setStatus("ready");
        },
        (err: unknown) => {
          if (!cancelled) setStatus(err instanceof ApiError && err.status === 404 ? "notfound" : "error");
        },
      );
    void refresh();

    const client = new SseClient({
      fetch: api.fetchImpl,
      url: streamUrl(`/api/v1/topics/${id}/stream`),
      onGap: () => {
        void refresh();
      },
      onEvent: (msg) => {
        let data: unknown;
        try {
          data = JSON.parse(msg.data);
        } catch {
          return;
        }
        if (msg.event === "comment.created") {
          const c = data as CommentDto;
          setComments((list) => (list.some((x) => x.id === c.id) ? list : [...list, c]));
          setTopic((t) => (t ? { ...t, counts: { ...t.counts, [c.kind]: t.counts[c.kind] + 1 } } : t));
        } else if (msg.event === "comment.hidden") {
          const h = data as HiddenDto;
          setComments((list) => list.filter((x) => x.id !== h.comment_id));
        } else if (msg.event === "topic.stamped") {
          const s = data as StampDto;
          setTopic((t) => (t ? { ...t, stamp_count: Math.max(t.stamp_count, s.stamp_count), level: s.level } : t));
        } else if (msg.event === "topic.hidden") {
          setStatus("notfound");
        }
      },
    });
    client.start();
    return () => {
      cancelled = true;
      client.stop();
    };
  }, [api, id, streamUrl]);

  return { topic, comments, status, setTopic };
}
